import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/context/AuthContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { Toaster } from "sonner";
import CookieConsent from "@/components/CookieConsent";
import ScrollToTop from "@/components/ScrollToTop";
import GaPageviews from "@/components/GaPageviews";

import Landing from "@/pages/Landing";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import Contact from "@/pages/Contact";
import About from "@/pages/About";
import FundingGuide from "@/pages/FundingGuide";
import Welcome from "@/pages/Welcome";
import ProvidersIndex from "@/pages/providers/ProvidersIndex";
import ProviderOrCategory from "@/pages/providers/ProviderOrCategory";
import CategoryLanding from "@/pages/providers/CategoryLanding";
import ProviderRegister from "@/pages/provider/ProviderRegister";
import ProviderDashboard from "@/pages/provider/ProviderDashboard";
import AdminMarketplace from "@/pages/admin/AdminMarketplace";
import AdminProviderInvites from "@/pages/admin/AdminProviderInvites";
import GlossaryIndex from "@/pages/glossary/GlossaryIndex";
import GlossaryTerm from "@/pages/glossary/GlossaryTerm";
import CategoryHub from "@/pages/glossary/CategoryHub";
import Login from "@/pages/auth/Login";
import OpsLogin from "@/pages/auth/OpsLogin";
import Register from "@/pages/auth/Register";
import ForgotPassword from "@/pages/auth/ForgotPassword";
import ResetPassword from "@/pages/auth/ResetPassword";
import VerifyEmail from "@/pages/auth/VerifyEmail";

import AppLayout from "@/components/layout/AppLayout";
import AdminLayout from "@/components/layout/AdminLayout";

import Onboarding from "@/pages/app/Onboarding";
import Dashboard from "@/pages/app/Dashboard";
import Roadmap from "@/pages/app/Roadmap";
import FundingFinder from "@/pages/app/FundingFinder";
import ProgramDetail from "@/pages/app/ProgramDetail";
import AiNavigator from "@/pages/app/AiNavigator";
import Documents from "@/pages/app/Documents";
import Journal from "@/pages/app/Journal";
import Tasks from "@/pages/app/Tasks";
import Account from "@/pages/app/Account";
import AskAnything from "@/pages/app/AskAnything";
import AskThread from "@/pages/app/AskThread";
import ToolsGuide from "@/pages/app/ToolsGuide";

import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminPrograms from "@/pages/admin/AdminPrograms";
import AdminRules from "@/pages/admin/AdminRules";
import AdminGuardrails from "@/pages/admin/AdminGuardrails";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminUserDetail from "@/pages/admin/AdminUserDetail";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminLeads from "@/pages/admin/AdminLeads";
import AdminCoupons from "@/pages/admin/AdminCoupons";
import AdminAskAnything from "@/pages/admin/AdminAskAnything";
import AdminAskThread from "@/pages/admin/AdminAskThread";
import AdminAudit from "@/pages/admin/AdminAudit";
import ArticleStudio from "@/pages/admin/ArticleStudio";
import ArticleStudioNew from "@/pages/admin/ArticleStudioNew";
import ArticleEditor from "@/pages/admin/ArticleEditor";
import ArticlesIndex from "@/pages/articles/ArticlesIndex";
import ArticleDetail from "@/pages/articles/ArticleDetail";

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <GaPageviews />
        <Toaster position="top-center" richColors />
        <CookieConsent />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms-of-service" element={<Terms />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/about" element={<About />} />
          <Route path="/funding-guide" element={<FundingGuide />} />
          <Route path="/welcome" element={<Welcome />} />
          <Route path="/providers" element={<ProvidersIndex />} />
          <Route path="/providers/:slug" element={<ProviderOrCategory />} />
          <Route path="/providers/:category/:city" element={<CategoryLanding />} />
          <Route path="/provider/register" element={<ProviderRegister />} />
          <Route path="/provider/dashboard" element={<ProtectedRoute><ProviderDashboard /></ProtectedRoute>} />
          <Route path="/terms" element={<GlossaryIndex />} />
          <Route path="/terms/category/:key" element={<CategoryHub />} />
          <Route path="/terms/:slug" element={<GlossaryTerm />} />
          <Route path="/articles" element={<ArticlesIndex />} />
          <Route path="/articles/:slug" element={<ArticleDetail />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify-email" element={<VerifyEmail />} />

          <Route path="/app/onboarding" element={<ProtectedRoute><Onboarding /></ProtectedRoute>} />
          <Route path="/app" element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
            <Route index element={<Dashboard />} />
            <Route path="roadmap" element={<Roadmap />} />
            <Route path="funding" element={<FundingFinder />} />
            <Route path="funding/:id" element={<ProgramDetail />} />
            <Route path="navigator" element={<AiNavigator />} />
            <Route path="documents" element={<Documents />} />
            <Route path="journal" element={<Journal />} />
            <Route path="tasks" element={<Tasks />} />
            <Route path="ask" element={<AskAnything />} />
            <Route path="ask/:id" element={<AskThread />} />
            <Route path="account" element={<Account />} />
            <Route path="guide" element={<ToolsGuide />} />
          </Route>

          <Route path="/ops/login" element={<OpsLogin />} />
          <Route path="/ops" element={<ProtectedRoute adminOnly><AdminLayout /></ProtectedRoute>}>
            <Route index element={<AdminDashboard />} />
            <Route path="programs" element={<AdminPrograms />} />
            <Route path="rules" element={<AdminRules />} />
            <Route path="guardrails" element={<AdminGuardrails />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="users/:id" element={<AdminUserDetail />} />
            <Route path="messages" element={<AdminMessages />} />
            <Route path="leads" element={<AdminLeads />} />
            <Route path="coupons" element={<AdminCoupons />} />
            <Route path="ask-anything" element={<AdminAskAnything />} />
            <Route path="ask-anything/:id" element={<AdminAskThread />} />
            <Route path="marketplace" element={<AdminMarketplace />} />
            <Route path="marketplace/invites" element={<AdminProviderInvites />} />
            <Route path="audit" element={<AdminAudit />} />
            <Route path="article-studio" element={<ArticleStudio />} />
            <Route path="article-studio/new" element={<ArticleStudioNew />} />
            <Route path="article-studio/edit/:id" element={<ArticleEditor />} />
          </Route>
          <Route path="/ops/article-studio/preview/:id" element={<ProtectedRoute adminOnly><ArticleDetail preview /></ProtectedRoute>} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
